import './fabricDefaults.ts';
import {
    CustomFabricObject,
    ElementData,
    EventTypes,
    ObjectTypes,
    VideoElement
} from './types.ts';
import {
    ActiveSelection,
    Canvas,
    Circle,
    FabricImage,
    FabricObject,
    Group,
    IText,
    LayoutManager,
    Path,
    Point,
    Rect,
    Textbox,
    util
} from 'fabric';
import {ReactStateSetter, UnknownObject} from '../../types.ts';
import {v4 as uuidv4} from 'uuid';
import {FabricVideo} from './video.ts';
import {Sprite} from './sprite.ts';
import {StickyNote, StickyNoteOptions} from './stickyNote.ts';
import {ResourceCard, ResourceCardOptions} from './resourceCard.ts';
import {ProjectCard, ProjectCardOptions} from './projectCard.ts';
import {LinkableImage} from './linkableImage.ts';
import {compareOrdinal} from '../../project_data/elementDataSorter.ts';

export function isCustomFabricObject(obj: unknown): obj is CustomFabricObject {
    return obj instanceof FabricObject && 'elementId' in obj;
}

/*
 * Whether an object stays clickable in read-only and pan modes, where
 * everything else stops receiving events: anything with nav set (project and
 * resource cards) and any picture that is a working link.
 */
export const isClickable = (object: FabricObject): boolean =>
    Boolean((object as CustomFabricObject).nav) ||
    (object instanceof LinkableImage && object.isLinkActive);

export const findFabricObject = (canvas: Canvas, elementId: string) => {
    const objects = canvas.getObjects();
    return objects.find(
        object => (object as CustomFabricObject).elementId === elementId
    );
};

/*
 * Returns the Fabric 5 type string for an object. v6+ made instance `type` a
 * legacy accessor whose casing no longer matches what v5 saved ('itext' vs
 * 'i-text', 'activeselection' vs 'activeSelection'), so derive it from the
 * class instead. Order matters: subclasses before their base classes.
 */
export const getObjectType = (obj: FabricObject): string => {
    if (obj instanceof Sprite) {
        return 'sprite';
    }
    if (obj instanceof FabricVideo) {
        return ObjectTypes.Video;
    }
    if (obj instanceof FabricImage) {
        return ObjectTypes.Image;
    }
    if (obj instanceof ResourceCard) {
        return ObjectTypes.ResourceCard;
    }
    if (obj instanceof ProjectCard) {
        return ObjectTypes.ProjectCard;
    }
    if (obj instanceof StickyNote) {
        return ObjectTypes.StickyNote;
    }
    if (obj instanceof Textbox) {
        return 'textbox';
    }
    if (obj instanceof IText) {
        return ObjectTypes.Text;
    }
    if (obj instanceof ActiveSelection) {
        return ObjectTypes.ActiveSelection;
    }
    if (obj instanceof Group) {
        return ObjectTypes.Group;
    }
    if (obj instanceof Rect) {
        return ObjectTypes.Rect;
    }
    if (obj instanceof Circle) {
        return ObjectTypes.Circle;
    }
    if (obj instanceof Path) {
        return 'path';
    }
    return obj.type;
};

const legacyTypeNames: Record<string, string> = {
    Rect: 'rect',
    Circle: 'circle',
    Ellipse: 'ellipse',
    Triangle: 'triangle',
    Line: 'line',
    Polygon: 'polygon',
    Polyline: 'polyline',
    Path: 'path',
    Text: 'text',
    IText: 'i-text',
    Textbox: 'textbox',
    Image: 'image',
    Group: 'group',
    ActiveSelection: 'activeSelection'
};

/*
 * v6+ serializes `type` as the PascalCase class name and groups gain a
 * `layoutManager` entry. Persisted element data must keep the v5 shape
 * (lowercase types, no layout manager) so that boards stay readable by this
 * engine's restore switch and consistent with pre-migration data.
 */
export const normalizeSerializedElement = (
    data: UnknownObject
): UnknownObject => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const {layoutManager, ...result} = data;

    if (typeof result.type === 'string' && result.type in legacyTypeNames) {
        result.type = legacyTypeNames[result.type];
    }

    if (Array.isArray(result.objects)) {
        result.objects = result.objects.map(entry =>
            normalizeSerializedElement(entry as UnknownObject)
        );
    }

    if (
        result.clipPath &&
        typeof result.clipPath === 'object' &&
        !Array.isArray(result.clipPath)
    ) {
        result.clipPath = normalizeSerializedElement(
            result.clipPath as UnknownObject
        );
    }

    return result;
};

/*
 * fabric.util.transformPoint equivalent; inlined so we don't depend on util
 * relocations between major versions.
 */
export const transformPoint = (
    point: {x: number; y: number},
    matrix: number[]
): Point =>
    new Point(
        matrix[0] * point.x + matrix[2] * point.y + matrix[4],
        matrix[1] * point.x + matrix[3] * point.y + matrix[5]
    );

/*
 * Where an object inside an ActiveSelection really is on the board. Its
 * own left/top/scale/angle are relative to the selection's centre while it
 * is selected, so toObject() on it would store the selection-relative
 * values; these are the board ones.
 */
export const absoluteGeometry = (
    object: FabricObject,
    selection: FabricObject
): {
    left: number;
    top: number;
    scaleX: number;
    scaleY: number;
    angle: number;
} => {
    const scaleX = selection.scaleX * object.scaleX;
    const scaleY = selection.scaleY * object.scaleY;

    /*
     * Fabric measures an object from its top-left corner to its centre with
     * the stroke included (half of it each side), so the corner is half a
     * stroke further out than the width alone says. A uniform stroke isn't
     * scaled with the object, so in the object's own plane it is smaller by
     * the scale.
     */
    const strokeX = object.strokeUniform
        ? object.strokeWidth / (scaleX || 1)
        : object.strokeWidth;
    const strokeY = object.strokeUniform
        ? object.strokeWidth / (scaleY || 1)
        : object.strokeWidth;

    const topLeft = transformPoint(
        {
            x: -(object.width + strokeX) / 2,
            y: -(object.height + strokeY) / 2
        },
        object.calcTransformMatrix()
    );

    return {
        scaleX,
        scaleY,
        angle: selection.angle + object.angle,
        left: topLeft.x,
        top: topLeft.y
    };
};

export const fireObjectModified = (canvas: Canvas, target: FabricObject) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    canvas.fire(EventTypes.ObjectModified as any, {target});
};

/*
 * v6+ serializes text styles as an array of ranges; v5 saved a per-character
 * nested object, which is still the runtime format. Accepts both.
 */
const toRuntimeStyles = (styles: unknown, text: string): unknown => {
    if (!styles) {
        return styles;
    }
    return util.stylesFromArray(
        styles as Parameters<typeof util.stylesFromArray>[0],
        text
    );
};

/*
 * Restored groups already carry their saved geometry: child coordinates are
 * relative to the group's centre and left/top/width/height are persisted.
 * Skip the "initialization" layout pass that would otherwise recompute them
 * (this mirrors what Group.fromObject does internally with a noop manager).
 */
class RestoredGroupLayoutManager extends LayoutManager {
    performLayout(context: Parameters<LayoutManager['performLayout']>[0]) {
        if (context.type === 'initialization') {
            return;
        }
        super.performLayout(context);
    }
}

// TODO: image filters

/*
 * A picture or video added by an agent, whose size the server couldn't read,
 * carries the box it should fill (fitWidth, and fitHeight or keep its shape).
 * Once loaded it is scaled into that box; the next save stores the real size.
 */
const fitToBox = (
    object: FabricObject,
    fitWidth: unknown,
    fitHeight: unknown
) => {
    if (typeof fitWidth !== 'number' || !object.width || !object.height) {
        return;
    }

    const scaleX = fitWidth / object.width;
    const scaleY =
        typeof fitHeight === 'number' ? fitHeight / object.height : scaleX;

    object.set({scaleX, scaleY});
};

export const modifyFabricObject = (
    canvas: Canvas,
    object: FabricObject,
    elementId: string,
    elementData: UnknownObject
) => {
    const {type, version, ...properties} = elementData as ElementData;

    console.log(
        `Modifying type: ${type}, version: ${version}, elementId: ${elementId}`
    );

    if (type === 'settings') {
        const {canvasBackgroundColor} = elementData;

        if (typeof canvasBackgroundColor === 'string') {
            canvas.backgroundColor = canvasBackgroundColor;
            canvas.renderAll();
        }
    } else if (object instanceof ProjectCard) {
        object.applyElementProps(properties);
    } else if (object instanceof ResourceCard) {
        object.applyElementProps(properties);
    } else if (object instanceof StickyNote) {
        object.applyElementProps(properties);
    } else {
        if (properties.styles && object instanceof IText) {
            properties.styles = toRuntimeStyles(
                properties.styles,
                typeof properties.text === 'string'
                    ? properties.text
                    : object.text
            );
        }
        object.set(properties);
    }

    // Where the object can be clicked follows where it is drawn; set() alone leaves the old place.
    if (type !== 'settings') {
        object.setCoords();
    }
};

export const restoreFabricObject = (
    canvas: Canvas,
    elementId: string | undefined,
    elementData: UnknownObject,
    setVideoElements: ReactStateSetter<VideoElement[]>,
    isReadOnly: boolean,
    onRestoreToGroup?: (obj: FabricObject) => void
) => {
    if (elementId != undefined && findFabricObject(canvas, elementId)) {
        return;
    }

    const {type, version, ...properties} = elementData as ElementData;

    console.log(
        `Creating type: ${type}, version: ${version}, elementId: ${elementId}`
    );

    const onRestoreImpl = (restoredObject: FabricObject) => {
        restoredObject.selectable = !isReadOnly;

        if (isReadOnly) {
            if (!isClickable(restoredObject)) {
                restoredObject.evented = false;
            } else {
                restoredObject.evented = true;
                restoredObject.hoverCursor = 'pointer';
            }
        } else {
            restoredObject.evented = true;
        }

        /*
         * The object has been restored - but it may have been added out of order
         * due to it being an image or video callback
         * therefore we need to move it to it's correct position
         * within either the canvas or group
         * */

        if (onRestoreToGroup) {
            onRestoreToGroup(restoredObject);
        } else {
            canvas.add(restoredObject);
            fixObjectIndex(canvas, restoredObject);
        }
    };

    const fixObjectIndex = (canvas: Canvas, restoredObject: FabricObject) => {
        // Check if the object has been restored to the correct index
        // Objects can be loaded in an incorrect order due to being loaded in a callback
        // for loading video and image. This code corrects that after it's loaded.

        const canvasObjects = canvas.getObjects();
        const indexOfRestoredObject = canvasObjects.indexOf(restoredObject);
        const ordinalPosition = elementData.ordinalPosition as
            | number
            | undefined;

        /*
         * Scenario: [B, C, D] -> A is restored -> [B, C, D, A] (incorrect
         * order). A belongs before the first object that sorts after it, by
         * position and then id: the same tie-break the server and the
         * initial sort use, so a reload draws the board the same way every
         * time even where two objects share a position.
         */
        const firstElementSortedAfter = canvasObjects.findIndex(
            obj =>
                obj !== restoredObject &&
                compareOrdinal(
                    ordinalPosition,
                    (obj as CustomFabricObject).ordinalPosition,
                    elementId,
                    (obj as CustomFabricObject).elementId
                ) < 0
        );

        if (firstElementSortedAfter >= 0) {
            if (indexOfRestoredObject > firstElementSortedAfter) {
                canvas.moveObjectTo(restoredObject, firstElementSortedAfter);
            }
        }
    };

    switch (type) {
        case 'settings':
            {
                const {canvasBackgroundColor} = properties;

                if (typeof canvasBackgroundColor === 'string') {
                    canvas.backgroundColor = canvasBackgroundColor;
                    canvas.renderAll();
                }
            }
            break;
        case 'image':
            LinkableImage.fromURL(properties.src as string, {
                crossOrigin: 'anonymous'
            })
                .then(img => {
                    const {clipPath, fitWidth, fitHeight, ...imageProperties} =
                        properties;

                    if (clipPath !== undefined) {
                        const clipPathObject = clipPath as UnknownObject;

                        const {
                            type: clipPathObjectType,
                            // eslint-disable-next-line @typescript-eslint/no-unused-vars
                            version: clipPathObjectVersion,
                            ...clipPathObjectProperties
                        } = clipPathObject;

                        switch (clipPathObjectType) {
                            case 'rect':
                                {
                                    imageProperties.clipPath = new Rect(
                                        clipPathObjectProperties as ConstructorParameters<
                                            typeof Rect
                                        >[0]
                                    );
                                }
                                break;
                            case 'circle':
                                {
                                    imageProperties.clipPath = new Circle(
                                        clipPathObjectProperties as ConstructorParameters<
                                            typeof Circle
                                        >[0]
                                    );
                                }
                                break;
                        }
                    }

                    img.set(imageProperties);
                    fitToBox(img, fitWidth, fitHeight);
                    (img as unknown as CustomFabricObject).elementId =
                        elementId;
                    onRestoreImpl(img);
                })
                .catch(error => {
                    console.error(
                        `Failed to restore image elementId=${elementId}`,
                        error
                    );
                });
            break;

        case 'rect':
            {
                const rect = new Rect(
                    properties as ConstructorParameters<typeof Rect>[0]
                );
                (rect as unknown as CustomFabricObject).elementId = elementId;
                onRestoreImpl(rect);
            }
            break;

        case 'circle':
            {
                const circle = new Circle(
                    properties as ConstructorParameters<typeof Circle>[0]
                );
                (circle as unknown as CustomFabricObject).elementId = elementId;
                onRestoreImpl(circle);
            }
            break;

        case 'i-text':
            {
                const text = properties.text as string;
                const iText = new IText(text, {
                    ...properties,
                    styles: toRuntimeStyles(properties.styles, text)
                } as ConstructorParameters<typeof IText>[1]);
                (iText as unknown as CustomFabricObject).elementId = elementId;
                onRestoreImpl(iText);
            }
            break;

        case 'path':
            {
                const pathString = properties.path as string;
                const path = new Path(
                    pathString,
                    properties as ConstructorParameters<typeof Path>[1]
                );
                (path as unknown as CustomFabricObject).elementId = elementId;
                onRestoreImpl(path);
            }
            break;

        case 'video':
            // Create a video element
            {
                const videoElement: HTMLVideoElement =
                    document.createElement('video');
                videoElement.src = properties.src as string;
                videoElement.setAttribute('crossOrigin', 'anonymous');
                videoElement.load();
                videoElement.onloadeddata = () => {
                    // Update video element dimensions
                    videoElement.width = videoElement.videoWidth;
                    videoElement.height = videoElement.videoHeight;

                    // Create a Fabric image object from the video element
                    const {fitWidth, fitHeight, ...videoProperties} =
                        properties;
                    const video = new FabricVideo(videoElement, {
                        ...videoProperties, // spread other properties
                        objectCaching: false
                    } as ConstructorParameters<typeof FabricVideo>[1]);
                    fitToBox(video, fitWidth, fitHeight);

                    // Cast to custom Fabric object and add custom properties
                    const customVideo: CustomFabricObject =
                        video as unknown as CustomFabricObject;
                    customVideo.elementId = elementId;
                    customVideo.uri = properties.src as string;
                    customVideo.videoElement = videoElement;

                    videoElement.currentTime = 0;
                    onRestoreImpl(customVideo);

                    // Setup animation frame for rendering the video on the canvas
                    requestAnimationFrame(function render() {
                        canvas.renderAll();
                        requestAnimationFrame(render);
                    });

                    // Notify CanvasComponent about the new video element
                    setVideoElements(
                        (previousVideoElements: VideoElement[]) => {
                            return [
                                ...previousVideoElements,
                                {id: elementId!, element: videoElement}
                            ];
                        }
                    );

                    // document.dispatchEvent(new CustomEvent('videoElementAdded', { detail: { elementId, videoElement } }));
                    fireObjectModified(canvas, video);
                };
            }
            break;

        case 'project-card':
            {
                const card = new ProjectCard(properties as ProjectCardOptions);
                (card as unknown as CustomFabricObject).elementId = elementId;
                // Added straight away so z-order is right; the thumbnail
                // and avatar fill in when they arrive.
                onRestoreImpl(card);
                void card.loadImages();
            }
            break;

        case 'resource-card':
            {
                const card = new ResourceCard(
                    properties as ResourceCardOptions
                );
                (card as unknown as CustomFabricObject).elementId = elementId;
                onRestoreImpl(card);
            }
            break;

        case 'sticky-note':
            {
                const note = new StickyNote(properties as StickyNoteOptions);
                (note as unknown as CustomFabricObject).elementId = elementId;
                onRestoreImpl(note);
            }
            break;

        case 'textbox':
            {
                const {text, ...textboxProperties} = properties;
                const textbox = new Textbox(
                    text as string,
                    {
                        ...textboxProperties,
                        styles: toRuntimeStyles(
                            textboxProperties.styles,
                            text as string
                        )
                    } as ConstructorParameters<typeof Textbox>[1]
                );
                (textbox as unknown as CustomFabricObject).elementId =
                    elementId;
                onRestoreImpl(textbox);
            }
            break;

        case 'group':
            {
                const groupObjects: FabricObject[] = [];

                if (Array.isArray(properties.objects)) {
                    const restorePromises = properties.objects.map(
                        (obj: UnknownObject, index: number) => {
                            return new Promise<void>(resolve => {
                                const groupObjectElementId =
                                    'elementId' in obj &&
                                    typeof obj.elementId == 'string'
                                        ? obj.elementId
                                        : uuidv4();
                                restoreFabricObject(
                                    canvas,
                                    groupObjectElementId,
                                    obj,
                                    setVideoElements,
                                    isReadOnly,
                                    (restoredObj: FabricObject) => {
                                        groupObjects[index] = restoredObj;
                                        resolve();
                                    }
                                );
                            });
                        }
                    );

                    Promise.all(restorePromises).then(() => {
                        const {
                            // eslint-disable-next-line @typescript-eslint/no-unused-vars
                            objects,
                            // eslint-disable-next-line @typescript-eslint/no-unused-vars
                            layoutManager,
                            ...groupProperties
                        } = properties;
                        const group = new Group(groupObjects, {
                            ...groupProperties,
                            layoutManager: new RestoredGroupLayoutManager()
                        } as ConstructorParameters<typeof Group>[1]);
                        (group as unknown as CustomFabricObject).elementId =
                            elementId;
                        group.setCoords();
                        onRestoreImpl(group);
                    });
                }
            }
            break;

        default:
            /*
             * An element type this build does not know about: most likely a
             * board saved by a newer client. Skipping it leaves the stored
             * element untouched, but say so, because the alternative is an
             * object silently missing from the board with no clue why.
             */
            console.warn(
                'Skipping unknown whiteboard element type: ' +
                    type +
                    ', elementId: ' +
                    elementId
            );
            break;
    }
};
